import { Game } from "@/models/Game";
import { ManhuntMapPoi } from "@/models/MapPois/ManHunt/ManhuntMapPoi";
import { DateTime } from "luxon";
import { getGoogleMapsAPI } from "gmap-vue";
import { QuestMapPoi } from "@/models/MapPois/Quest/QuestMapPoi";
import { Q_QUEST_TYPE } from "@/models/MapPois/Quest/QuestsEnum";
import { map } from "lodash";

export default class DateHelper {
  static getDatesInUTC(days: number | 0): string {
    const date = new Date();
    date.setUTCHours(0, 0, 0, 0);
    if (days != 0) {
      date.setDate(date.getDate() - days);
    }
    return date.toISOString();
  }

  static async getCountry(lat: number, long: number): Promise<any> {
    try {
      const map = getGoogleMapsAPI;
      const google = await map();
      const geocoder = new google.maps.Geocoder();
      const location = new google.maps.LatLng(lat, long);
      return await geocoder.geocode({ location: location });
    } catch (error) {
      console.log(error);
      return "";
    }
  }

  static getUTCTime(date: string, time: string, timezone: any): string {
    const time2 = DateTime.fromISO(time);
    return DateTime.fromISO(date.toString(), {
      zone: timezone.timeZoneId,
    })
      .set({
        hour: time2.hour,
        minute: time2.minute,
        second: 0,
        millisecond: 0,
      })
      .toISO();
  }

  static getMapImage(mapPoi: ManhuntMapPoi): string {
    if (mapPoi.key === "START_ZONE") {
      return require("@/assets/images/map/start-zone-blue.svg");
    } else if (mapPoi.map_mods.active && mapPoi.map_mods.type === "runaway") {
      return require("@/assets/images/map/runaway-purple.svg");
    } else if (mapPoi.map_mods.active && mapPoi.map_mods.type === "trigger") {
      return require("@/assets/images/map/trigger-green.svg");
    } else if (mapPoi.map_mods.targetedBy?.runaway !== undefined) {
      return require("@/assets/images/map/target-purple.svg");
    } else if (mapPoi.map_mods.targetedBy?.trigger !== undefined) {
      return require("@/assets/images/map/target-green.svg");
    } else if (mapPoi.witness.active) {
      return require("@/assets/images/map/witness-pink.svg");
    } else if (mapPoi.visible) {
      return require("@/assets/images/map/question-pink.svg");
    } else if (!mapPoi.visible) {
      return require("@/assets/images/map/hidden-pink.svg");
    } else {
      return require("@/assets/images/map/map-pin-pink.svg");
    }
  }
  static getGreyMapImage(mapPoi: ManhuntMapPoi): string {
    if (mapPoi.key === "START_ZONE") {
      return require("@/assets/images/map/start-zone-gray.svg");
    } else if (mapPoi.map_mods.active && mapPoi.map_mods.type === "runaway") {
      return require("@/assets/images/map/runaway-gray.svg");
    } else if (mapPoi.map_mods.active && mapPoi.map_mods.type === "trigger") {
      return require("@/assets/images/map/trigger-gray.svg");
    } else if (mapPoi.map_mods.targetedBy?.runaway !== undefined) {
      return require("@/assets/images/map/target-gray.svg");
    } else if (mapPoi.map_mods.targetedBy?.trigger !== undefined) {
      return require("@/assets/images/map/target-gray.svg");
    } else if (mapPoi.witness.active) {
      return require("@/assets/images/map/witness-gray.svg");
    } else if (mapPoi.visible) {
      return require("@/assets/images/map/question-gray.svg");
    } else if (!mapPoi.visible) {
      return require("@/assets/images/map/hidden-gray.svg");
    } else {
      return require("@/assets/images/map/map-pin-gray.svg");
    }
  }

  static getMapQuestImage(mapPoi: QuestMapPoi): string {
    if (mapPoi.key === "START_ZONE") {
      return require("@/assets/images/map/start-zone-blue.svg");
    } else if (mapPoi.type === "person") {
      return require("@/assets/images/map/person-pink.svg");
    } else if (mapPoi.type === "quest") {
      return require("@/assets/images/map/quest-pink.svg");
    } else {
      return require("@/assets/images/map/map-pin-pink.svg");
    }
  }

  static getGreyMapQuestImage(mapPoi: QuestMapPoi): string {
    if (mapPoi.key === "START_ZONE") {
      return require("@/assets/images/map/start-zone-gray.svg");
    } else if (mapPoi.type === "person") {
      return require("@/assets/images/map/person-gray.svg");
    } else if (mapPoi.type === "quest") {
      return require("@/assets/images/map/quest-gray.svg");
    } else {
      return require("@/assets/images/map/map-pin-gray.svg");
    }
  }

  static async shuffle<T>(array: T[]): Promise<T[]> {
    let currentIndex = array.length,
      randomIndex;

    // While there remain elements to shuffle.
    while (currentIndex != 0) {
      // Pick a remaining element.
      randomIndex = Math.floor(Math.random() * currentIndex);
      currentIndex--;

      // And swap it with the current element.
      [array[currentIndex], array[randomIndex]] = [
        array[randomIndex],
        array[currentIndex],
      ];
    }

    return array;
  }

  static getStatus(data: Game, appPrelaunch: number): string {
    const now = new Date();
    const currentDate = new Date(
      now.getTime() - now.getTimezoneOffset() * 60000
    ).toISOString();
    const preLaunchDate = new Date(
      now.getTime() - appPrelaunch * 60000 - now.getTimezoneOffset() * 60000
    ).toISOString();
    const startDate = new Date(data.start_dt).toISOString();
    const endDate = new Date(data.end_dt).toISOString();
    if (data.template) {
      return "TEMPLATE";
    }
    if (data.live && currentDate >= preLaunchDate && startDate >= currentDate) {
      return "OPEN";
    }
    if (data.live && startDate <= currentDate && endDate > currentDate) {
      return "LIVE";
    }
    if (data.live && startDate > currentDate && endDate > currentDate) {
      return "PUBLISHED";
    }
    if (data.live && startDate <= currentDate && endDate <= currentDate) {
      return "ENDED";
    }
    if (!data.live && startDate <= currentDate && endDate <= currentDate) {
      return "PURGING";
    }
    return "WIP";
  }
}
